/** Öffentliches Wartelisten-Formular (E10.1) unter /w/:slug – ohne Login, mit Turnstile, wenn eingerichtet. */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Button, Illustration, Notice, Seitenzustand, TextField } from "../ui";
import { Marke } from "../ui/Marke";
import { api, ApiFehler, rufe } from "../api/client";
import { RechtlichesFuss } from "./Rechtliches";

interface Info {
  laden: { name: string; slug: string; telefon: string; logo: boolean; akzent: string | null };
  freieRegale: number;
  turnstileSiteKey: string | null;
}

export function WartelisteSeite() {
  const { slug = "" } = useParams();
  const q = useQuery({ queryKey: ["public", "warteliste", slug], queryFn: () => rufe<Info>(api.api.public.laden[":slug"].warteliste.$get({ param: { slug } })), retry: false });
  const [form, setForm] = useState({ name: "", email: "", telefon: "", wunsch: "", abTag: "", notiz: "" });
  const [token, setToken] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fertig, setFertig] = useState<number | null>(null);
  const turnstile = useRef<HTMLDivElement>(null);
  const siteKey = q.data?.turnstileSiteKey ?? null;
  useEffect(() => {
    const akzent = q.data?.laden.akzent ?? null;
    if (akzent) document.documentElement.style.setProperty("--accent", akzent);
    return () => {
      document.documentElement.style.removeProperty("--accent");
    };
  }, [q.data?.laden.akzent]);
  useEffect(() => {
    if (!siteKey || !turnstile.current) return;
    const w = window as unknown as { turnstile?: { render: (el: HTMLElement, o: { sitekey: string; callback: (t: string) => void; theme: string; language: string; size: string }) => void } };
    const rendern = () => w.turnstile?.render(turnstile.current!, { sitekey: siteKey, callback: setToken, theme: "auto", language: "de", size: "flexible" });
    if (w.turnstile) rendern();
    else {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.onload = rendern;
      document.head.appendChild(s);
    }
  }, [siteKey]);

  async function senden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (form.name.trim().length < 2) return setFehler("Bitte deinen Namen eingeben.");
    if (!form.email.trim() && !form.telefon.trim()) return setFehler("E-Mail oder Telefon, damit der Laden sich melden kann.");
    setLaeuft(true);
    try {
      const r = await rufe<{ position: number }>(api.api.public.laden[":slug"].warteliste.$post({ param: { slug }, json: { ...form, email: form.email.trim(), abTag: form.abTag || null, turnstile: token ?? undefined } }));
      setFertig(r.position);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Das hat nicht geklappt. Bitte noch einmal versuchen.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="app-public">
      <Seitenzustand query={q} lines={5}>
        {(d) =>
          fertig !== null ? (
            <div className="app-public__card sc-center" style={{ padding: 0 }}>
              <Illustration name="all-done" />
              <h1 className="display">Du stehst auf der Liste</h1>
              <p>Platz {fertig} bei {d.laden.name}. Sobald ein Regal frei wird, meldet sich der Laden bei dir.</p>
              <RechtlichesFuss />
            </div>
          ) : (
            <form className="app-public__card" onSubmit={(e) => void senden(e)} noValidate>
              <div className="app-public__brand">
                {d.laden.logo ? <img src={`/api/public/laden/${d.laden.slug}/logo`} alt="" className="app-logo" width={40} height={40} /> : <Marke size={40} />}
                <span className="sc-strong">{d.laden.name}</span>
              </div>
              <h1 className="title">Regal mieten – auf die Warteliste</h1>
              <p className="sc-muted">{d.freieRegale > 0 ? `Gerade ${d.freieRegale === 1 ? "ist ein Regal" : `sind ${d.freieRegale} Regale`} frei – trag dich ein, der Laden meldet sich.` : "Alle Regale sind gerade vermietet. Trag dich ein, du bekommst Bescheid, sobald eines frei wird."}</p>
              <TextField label="Dein Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" autoFocus />
              <TextField label="E-Mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" inputMode="email" />
              <TextField label="Telefon (optional)" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} autoComplete="tel" inputMode="tel" />
              <TextField label="Was möchtest du verkaufen, welche Regalgröße?" value={form.wunsch} onChange={(e) => setForm({ ...form, wunsch: e.target.value })} maxLength={120} />
              <TextField label="Frühestens ab" type="date" value={form.abTag} onChange={(e) => setForm({ ...form, abTag: e.target.value })} />
              {siteKey ? <div ref={turnstile} /> : null}
              {fehler ? <Notice tone="error">{fehler}</Notice> : null}
              <Button type="submit" variant="primary" size="lg" block loading={laeuft} disabled={!!siteKey && !token}>Eintragen</Button>
              <p className="sc-muted sc-small">Deine Angaben sieht nur der Laden und nutzt sie, um sich bei dir zu melden.</p>
              <RechtlichesFuss />
            </form>
          )
        }
      </Seitenzustand>
    </div>
  );
}
